import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';

import Button from '@components/Button';
import SvgIconArrowDown from '@components/Images/Rebranding/IconArrowDown';
import IconTI from '@components/Images/Rebranding/IconTI';
import SearchIcon from '@components/Images/SearchIcon';
import { ONBOARDING_STEP_IDS } from '@components/UserOnboardingTooltip/constants';
import {
  registerMoreNavRef,
  registerOnboardingAnchor,
  registerProNavRef,
  registerSettingsNavRef,
} from '@components/UserOnboardingTooltip/utils';
import { useEventTracking, useScrollingOverflow } from '@hooks';
import useUserOnboardingTooltip from '@hooks/useUserOnboardingTooltip';
import useWindowSize from '@hooks/useWindowSize';
import {
  deep_research_path,
  events_path,
  finance_path,
  graphics_path,
  new_session_path,
  org_charts_path,
  pro_path,
  root_path,
  search_page_path,
  technology_path,
  titv_path,
  weekend_path,
} from '@routes';
import { ClickTarget, click } from '@tracking';
import { NavCurrentUser } from '@utils/backend-api/currentUser';

import DefaultSubmenuNav from '../SubmenuNav/DefaultSubmenuNav';
import DefaultSubmenuNavItem from '../SubmenuNav/DefaultSubmenuNav/NavItem';
import SettingsSubmenuNav from '../SubmenuNav/SettingsSubmenuNav';
import SettingsSubmenuFooter from '../SubmenuNav/SettingsSubmenuNav/Footer';
import SettingsSubmenuNavItem from '../SubmenuNav/SettingsSubmenuNav/NavItem';
import { SubmenuNavRef } from '../SubmenuNav/index';
import {
  COMMUNITY_SUBMENU_NAV_ITEMS,
  MORE_SUBMENU_MAIN_NAV_ITEMS,
  MORE_SUBMENU_SOCIAL_NAV_ITEMS,
  MORE_SUBNAV_FOOTER_NAV_ITEMS,
  ProNavSections,
  SETTINGS_SUBNAV_MAIN_NAV_ITEMS,
} from '../navItems';
import NavItem from './NavItem';
import NavItemList from './NavItemList';

type TopNavProps = {
  className?: string;
  currentUser: NavCurrentUser;
  isScrollStyle?: boolean;
  proNavSections: ProNavSections;
};

const THREE_COLUMN_PRO_NAV_THRESHOLD = 17;
const SCROLL_TEST_SELECTOR = 'ul';

const TopNav = ({ className, currentUser, isScrollStyle, proNavSections }: TopNavProps) => {
  const proNavRef = useRef<SubmenuNavRef>(null);
  const moreNavRef = useRef<SubmenuNavRef>(null);
  const settingsNavRef = useRef<SubmenuNavRef>(null);
  const { activeStep, isProNavForcedOpen, isMoreNavForcedOpen, isSettingsNavForcedOpen } =
    useUserOnboardingTooltip();

  useEffect(() => {
    if (proNavRef.current) {
      registerProNavRef(proNavRef.current);
    }
    return () => {
      registerProNavRef(null);
    };
  }, []);

  useEffect(() => {
    if (moreNavRef.current) {
      registerMoreNavRef(moreNavRef.current);
    }
    return () => {
      registerMoreNavRef(null);
    };
  }, []);

  useEffect(() => {
    if (settingsNavRef.current) {
      registerSettingsNavRef(settingsNavRef.current);
    }
    return () => {
      registerSettingsNavRef(null);
    };
  }, []);
  const { trackEvent } = useEventTracking();
  const { width: windowWidth } = useWindowSize();
  const { proMenuScrollRef } = useScrollingOverflow({
    scrollTestSelector: SCROLL_TEST_SELECTOR,
  });
  const dataToolsProMenuItems = proNavSections?.data_tools ?? [];
  const specialProMenuItems = proNavSections?.special ?? [];
  const proNavItems = currentUser.isProSubscriber
    ? dataToolsProMenuItems
    : [
        {
          name: 'About Pro',
          href: pro_path(),
        },
        ...dataToolsProMenuItems,
      ];
  const proNavItemCount = proNavItems.length - 1;

  return (
    <nav
      aria-label="global"
      className={clsx(className, 'hidden nav:block', {
        'mt-0': isScrollStyle,
        'relative mt-28 border-0 border-b border-solid border-black': !isScrollStyle,
      })}
    >
      <div
        className={clsx('flex items-center justify-between', {
          'm-auto max-w-container-lg': isScrollStyle,
        })}
      >
        <NavItemList
          className={clsx('flex items-center', {
            'gap-8 md:gap-20 lg:gap-24': isScrollStyle,
            'md:!gap-20': windowWidth <= 1200 && isScrollStyle,
          })}
        >
          <a
            href={root_path()}
            className={clsx('h-40', {
              block: isScrollStyle,
              hidden: !isScrollStyle,
            })}
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Logo }))}
          >
            <IconTI className="h-full text-primary-magenta" />
          </a>
          <DefaultSubmenuNav
            ref={proNavRef}
            name="Pro"
            icon={(isOpen) => (
              <SvgIconArrowDown
                className={clsx('w-[19px] fill-current transition-transform', {
                  'rotate-180 group-hover:-translate-y-2': isOpen,
                  'group-hover:translate-y-2': !isOpen,
                })}
              />
            )}
            as="button"
            isScrollStyle={isScrollStyle}
            isPro
            openOnHover
            forceOpen={isProNavForcedOpen}
            className={clsx('group flex items-center gap-6 bg-pro-gold text-white', {
              'bg-transparent px-10 transition-none': isScrollStyle,
            })}
          >
            <div className="relative flex h-full items-start">
              <div
                className={clsx(
                  'mr-24 flex h-full flex-col scroll-smooth border-0 border-r border-solid border-pro-gold pr-24 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-56 after:bg-[linear-gradient(to_top,#2f3538_0%,rgba(0,0,0,0)_50%)]',
                  {
                    'after:content-[""] lg:after:content-none':
                      proNavItemCount >= THREE_COLUMN_PRO_NAV_THRESHOLD,
                  }
                )}
                ref={proMenuScrollRef}
              >
                <h2 className="mt-0 font-suisse-intl-mono text-overline-md text-pro-gold">
                  Data Tools
                </h2>
                <NavItemList
                  className={clsx(
                    'relative flex max-h-full w-max flex-col flex-wrap gap-x-8 gap-y-16 overflow-y-auto md:grid-cols-2 nav:grid nav:max-h-[calc(90vh_-_175px)] nav:grid-cols-1 nav:overflow-y-auto nav:overflow-x-hidden',
                    {
                      'lg:grid-cols-3': proNavItemCount >= THREE_COLUMN_PRO_NAV_THRESHOLD,
                    }
                  )}
                >
                  {proNavItems.map(({ name, ...rest }) => (
                    <DefaultSubmenuNavItem
                      as="link"
                      key={name}
                      onClickCapture={() =>
                        trackEvent(click({ clickTarget: ClickTarget.ProMenuItem, clickText: name }))
                      }
                      className="block w-[245px] leading-[1.3]"
                      {...rest}
                    >
                      {name}
                    </DefaultSubmenuNavItem>
                  ))}
                </NavItemList>
              </div>
              <div className="flex h-full flex-col">
                <h2 className="mt-0 font-suisse-intl-mono text-overline-md text-pro-gold">
                  Special Projects
                </h2>
                <NavItemList className="space-y-16">
                  {specialProMenuItems.map(({ name, ...rest }) => (
                    <DefaultSubmenuNavItem
                      as="link"
                      key={name}
                      onClickCapture={() =>
                        trackEvent(click({ clickTarget: ClickTarget.ProMenuItem, clickText: name }))
                      }
                      className="block w-[245px] leading-[1.3]"
                      {...rest}
                    >
                      {name}
                    </DefaultSubmenuNavItem>
                  ))}
                </NavItemList>
              </div>
            </div>
          </DefaultSubmenuNav>
          <NavItem
            as="link"
            className={clsx('pr-0', {
              'pl-16': !isScrollStyle,
              'pl-0': isScrollStyle,
            })}
            href={org_charts_path()}
            isScrollStyle={isScrollStyle}
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.OrgCharts }))}
          >
            Org Charts
          </NavItem>
          {isScrollStyle && (
            <NavItem
              as="link"
              className={clsx('pr-0', {
                'pl-16': !isScrollStyle,
                'pl-0': isScrollStyle,
              })}
              href={deep_research_path()}
              isScrollStyle={isScrollStyle}
              onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.OrgCharts }))}
            >
              Deep Research
            </NavItem>
          )}
        </NavItemList>
        <NavItemList
          className={clsx('flex items-center gap-24', {
            'xs:gap-8 md:gap-24': isScrollStyle,
            'md:!gap-20': windowWidth <= 1200 && isScrollStyle,
          })}
        >
          <NavItem
            as="link"
            href={technology_path()}
            isScrollStyle={isScrollStyle}
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Tech }))}
          >
            Tech
          </NavItem>
          <NavItem
            as="link"
            href={finance_path()}
            isScrollStyle={isScrollStyle}
            showLeftDivider={!isScrollStyle}
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Finance }))}
          >
            Finance
          </NavItem>
          <NavItem
            as="link"
            href={weekend_path()}
            isScrollStyle={isScrollStyle}
            showLeftDivider={!isScrollStyle}
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Weekend }))}
          >
            Weekend
          </NavItem>
          <NavItem
            as="link"
            href={graphics_path()}
            isScrollStyle={isScrollStyle}
            showLeftDivider={!isScrollStyle}
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Charts }))}
          >
            Charts
          </NavItem>
          <NavItem
            as="link"
            href={events_path()}
            isScrollStyle={isScrollStyle}
            showLeftDivider={!isScrollStyle}
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.EventsMenuItem }))}
          >
            Events
          </NavItem>
          <NavItem
            ref={(el) => registerOnboardingAnchor(ONBOARDING_STEP_IDS.Titv, el)}
            as="link"
            href={titv_path()}
            isScrollStyle={isScrollStyle}
            showLeftDivider={!isScrollStyle}
            liClassName="list-none [&::marker]:hidden"
            className={clsx({
              '!text-primary-magenta': activeStep === ONBOARDING_STEP_IDS.Titv,
            })}
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.TITV }))}
          >
            TITV
          </NavItem>
          <DefaultSubmenuNav
            name="Community"
            position="right"
            variant="small"
            as="button"
            className="bg-transparent text-black  hover:text-primary-magenta focus-visible:text-primary-magenta"
            isScrollStyle={isScrollStyle}
            openOnHover
            submenuClasses={clsx({ 'right-[-105px]': isScrollStyle })}
            styledWhenOpen
            showLeftDivider={!isScrollStyle}
          >
            <NavItemList className="space-y-24">
              {COMMUNITY_SUBMENU_NAV_ITEMS.map(({ name, ...rest }) => (
                <DefaultSubmenuNavItem
                  as="link"
                  key={name}
                  onClickCapture={() =>
                    trackEvent(
                      click({ clickTarget: ClickTarget.CommunityMenuItem, clickText: name })
                    )
                  }
                  {...rest}
                >
                  {name}
                </DefaultSubmenuNavItem>
              ))}
            </NavItemList>
          </DefaultSubmenuNav>
          <DefaultSubmenuNav
            ref={moreNavRef}
            name="More"
            position="right"
            variant="small"
            as="button"
            className="bg-transparent pl-8 pr-0 text-black hover:text-primary-magenta focus-visible:text-primary-magenta"
            isScrollStyle={isScrollStyle}
            openOnHover
            forceOpen={isMoreNavForcedOpen}
            submenuClasses={clsx({ 'right-[-135px]': isScrollStyle })}
            styledWhenOpen
            showLeftDivider={!isScrollStyle}
          >
            <NavItemList className="space-y-24">
              {MORE_SUBMENU_MAIN_NAV_ITEMS.map(({ name, ...rest }) => (
                <DefaultSubmenuNavItem
                  as="link"
                  key={name}
                  {...(name === 'Newsletters' ? { 'data-onboarding-anchor': 'newsletters' } : {})}
                  onClickCapture={() =>
                    trackEvent(click({ clickTarget: ClickTarget.MoreMenuItem, clickText: name }))
                  }
                  {...rest}
                >
                  {name}
                </DefaultSubmenuNavItem>
              ))}
            </NavItemList>
            <NavItemList className="mt-32 flex gap-16">
              {MORE_SUBMENU_SOCIAL_NAV_ITEMS.map(({ icon: Icon, name, ...rest }) => (
                <Button
                  className="flex h-[35px] w-[35px] items-center justify-center rounded-full border border-solid border-white bg-transparent text-white transition-colors hover:border-primary-magenta hover:text-primary-magenta focus:border-primary-magenta focus:text-primary-magenta"
                  as="link"
                  key={name}
                  onClickCapture={() =>
                    trackEvent(click({ clickTarget: ClickTarget.SocialNavItem, clickText: name }))
                  }
                  {...rest}
                >
                  <Icon className="h-12" />
                  <span className="sr-only">{name}</span>
                </Button>
              ))}
            </NavItemList>
            <NavItemList className="mt-32 flex justify-between">
              {MORE_SUBNAV_FOOTER_NAV_ITEMS.map(({ name, hasExternalLink, ...rest }) => (
                <li key={name}>
                  <Button
                    as={hasExternalLink ? 'externalLink' : 'link'}
                    className="font-suisse-screen text-disclosure-md leading-[1.3] text-white opacity-50 transition-all hover:text-primary-magenta hover:opacity-100 focus:opacity-100"
                    onClickCapture={() =>
                      trackEvent(
                        click({ clickTarget: ClickTarget.FinePrintNavItem, clickText: name })
                      )
                    }
                    {...rest}
                  >
                    {name}
                  </Button>
                </li>
              ))}
            </NavItemList>
          </DefaultSubmenuNav>
          {isScrollStyle && (
            <>
              <a
                href={search_page_path()}
                className="block px-10 py-[22px] leading-[0] text-white transition-colors hover:text-primary-magenta focus:text-primary-magenta"
                onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.SearchButton }))}
              >
                <SearchIcon className="w-20" />
              </a>
              {currentUser.isLoggedIn ? (
                <SettingsSubmenuNav
                  ref={settingsNavRef}
                  as="button"
                  avatarUrl={currentUser.avatarUrl}
                  forceOpen={isSettingsNavForcedOpen}
                  isProSubscriber={currentUser.isProSubscriber}
                  isScrollStyle={isScrollStyle}
                >
                  <NavItemList className="space-y-20">
                    {SETTINGS_SUBNAV_MAIN_NAV_ITEMS(currentUser).map(({ name, ...rest }) => (
                      <SettingsSubmenuNavItem
                        className="hover:text-primary-magenta focus:text-primary-magenta"
                        as="link"
                        key={name}
                        {...rest}
                      >
                        {name}
                      </SettingsSubmenuNavItem>
                    ))}
                  </NavItemList>
                  <hr className="my-20" />
                  <SettingsSubmenuFooter
                    avatarUrl={currentUser.avatarUrl}
                    profileCompletionPercentage={currentUser.profileCompletionPercentage}
                    profileCompletionStatuses={currentUser.profileCompletionStatuses}
                    username={currentUser.username}
                    initialSubscribeDateStr={currentUser.initialSubscribeDateStr}
                  />
                </SettingsSubmenuNav>
              ) : (
                <>
                  <Button
                    as="link"
                    className="py-[22px] pl-10 font-suisse-intl-mono text-overline-md text-white transition-colors hover:text-primary-magenta focus:text-primary-magenta"
                    href={new_session_path()}
                    onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.SignIn }))}
                  >
                    Sign in
                  </Button>
                </>
              )}
            </>
          )}
        </NavItemList>
      </div>
    </nav>
  );
};

export default TopNav;
